import type { Workspace } from '@stratumn/studio-shared';
import { Boxes } from 'lucide-react';
import { useMemo } from 'react';
import {
  type ComboboxOption,
  ComboboxPopover
} from '@/components/ui/combobox-popover';
import { FilterPill } from './FilterPill';
import { type FilterOperator, isMultiOperator } from './types';

type WorkspaceFilterPickerProps = {
  workspaces: Workspace[];
  selectedIds: string[];
  operator: FilterOperator;
  onChange: (ids: string[], operator: FilterOperator) => void;
  onRemove: () => void;
  /** When true, the value popover opens automatically on mount */
  autoOpen?: boolean;
};

export const WorkspaceFilterPicker = ({
  workspaces,
  selectedIds,
  operator,
  onChange,
  onRemove,
  autoOpen = false
}: WorkspaceFilterPickerProps) => {
  const isMulti = isMultiOperator(operator);

  const handleSelect = (id: string) => {
    if (!isMulti) {
      onChange([id], operator);
      return;
    }

    if (selectedIds.includes(id)) {
      onChange(
        selectedIds.filter(v => v !== id),
        operator
      );
    } else {
      onChange([...selectedIds, id], operator);
    }
  };

  const handleOperatorChange = (newOp: FilterOperator) => {
    if (!isMultiOperator(newOp) && selectedIds.length > 1) {
      onChange([selectedIds.at(-1)], newOp);
    } else {
      onChange(selectedIds, newOp);
    }
  };

  const selectedWorkspaces = workspaces.filter(w => selectedIds.includes(w.id));

  const options: ComboboxOption[] = useMemo(
    () =>
      workspaces.map(ws => ({
        value: ws.id,
        label: ws.name,
        render: (
          <div className="flex items-center gap-2">
            <div
              className="size-3 shrink-0 rounded-full"
              style={{ backgroundColor: ws.color || '#6366f1' }}
            />
            <span className="truncate">{ws.name}</span>
          </div>
        )
      })),
    [workspaces]
  );

  const renderValueSummary = () => {
    if (selectedWorkspaces.length === 0) {
      return <span className="text-muted-foreground">Select...</span>;
    }

    if (selectedWorkspaces.length === 1) {
      const ws = selectedWorkspaces[0];
      return (
        <div className="flex items-center gap-1.5">
          <div
            className="size-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: ws.color || '#6366f1' }}
          />
          <span className="max-w-24 truncate">{ws.name}</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1.5">
        <div
          className="size-2.5 shrink-0 rounded-full"
          style={{
            backgroundColor: selectedWorkspaces.at(-1).color || '#6366f1'
          }}
        />
        <span>{selectedWorkspaces.length} workspaces</span>
      </div>
    );
  };

  return (
    <FilterPill
      icon={<Boxes className="size-3.5" />}
      label="Workspace"
      onOperatorChange={handleOperatorChange}
      onRemove={onRemove}
      operator={operator}
    >
      <ComboboxPopover
        ariaLabel="Filter by workspace"
        defaultOpen={autoOpen}
        emptyMessage="No workspaces found."
        multi={isMulti}
        onSelect={handleSelect}
        options={options}
        searchable
        searchPlaceholder="Search workspaces..."
        selected={selectedIds}
        trigger={renderValueSummary()}
        width="w-64"
      />
    </FilterPill>
  );
};
